import { EMPTY_EVENT_FORM_VALUES } from './constants'
import { deriveEventLength, deriveEventLengthFromMinutes } from './eventLength'
import { getEventRecurrence } from './getEventRecurrence'
import type { EventFormValues, SnEvent } from './types'
import { SERIES_SCOPE_DEFAULT } from './seriesScope'

/** null/undefined → '' so inputs stay controlled. */
const str = (value: string | null | undefined): string => value ?? ''

/** null/undefined → '', numbers → their string form (the country/province
 *  inputs bind to string ids). */
const idStr = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value)

/**
 * The id for a country/province, from either shape the API sends.
 *
 * The event detail response nests the relation (`country: { id: 36, name: … }`)
 * and omits the flat `country_id` entirely; other responses send the flat id.
 * Reading only the flat key left the Country/Province selects blank on edit, so
 * both are accepted — flat first, since it is the more specific signal.
 */
const relationIdStr = (
  flatId: number | null | undefined,
  relation: unknown
): string => {
  if (flatId !== null && flatId !== undefined) {
    return String(flatId)
  }
  if (relation && typeof relation === 'object') {
    const { id } = relation as { id?: unknown }
    if (typeof id === 'number' || typeof id === 'string') {
      return String(id)
    }
  }
  return ''
}

/**
 * Build Formik initial values from an API event. Passing null/undefined yields
 * the blank add-mode state.
 *
 * `defaultTimezone` seeds the (non-visible) timezone field — the authoring
 * admin's account timezone, or the browser zone as a fallback — used for add
 * mode and whenever the event itself carries no timezone.
 */
export const eventToFormValues = (
  event: SnEvent | null | undefined,
  defaultTimezone = ''
): EventFormValues => {
  if (!event) {
    return {
      ...EMPTY_EVENT_FORM_VALUES,
      timezone: defaultTimezone,
      defaultTimezone,
    }
  }

  // Null for a one-off; otherwise the rule, read from `series` when the API
  // nests it there.
  const recurrence = getEventRecurrence(event)

  return {
    name: str(event.name),
    category: str(event.category),
    // The API returns the notes field as `details`; the form and payload call
    // it `description`. Create accepts both names, so the write path keeps its
    // own naming and only this boundary translates.
    description: str(event.details),
    starts_at: str(event.starts_at),
    ends_at: str(event.ends_at),
    // Matches a preset when the duration is exactly one, otherwise Custom —
    // never rounded, so a 45-minute event keeps its 45 minutes on save.
    // A repeating event states its length outright (`series.duration_minutes`),
    // which beats diffing this one occurrence's start/end. Falls back to the
    // diff for a one-off, or when the series omits it.
    eventLength: deriveEventLengthFromMinutes(
      recurrence?.durationMinutes,
      deriveEventLength(event.starts_at, event.ends_at)
    ),
    // The API does return a structured `address` (alongside venue/facility),
    // so edit mode repopulates the form's address fields from it.
    addressName: str(event.address_name),
    addressStreet1: str(event.address?.street_1),
    addressStreet2: str(event.address?.street_2),
    addressCity: str(event.address?.city),
    addressPostalCode: str(event.address?.postal_code),
    addressCountryId: relationIdStr(
      event.address?.country_id,
      event.address?.country
    ),
    addressProvinceId: relationIdStr(
      event.address?.province_id,
      event.address?.province
    ),
    // A venue-backed event reopens in VENUE mode — the Location select
    // preselected, the address fields and Timezone hidden. Without this it
    // would open in custom mode and silently unlink the venue on save.
    venueId: str(event.venue?.id),
    facilityId: str(event.facility?.id),
    // Pre-selects the Team field on edit. Seeded for BOTH kinds of event — a
    // team-owned one simply never renders the field, and its value is filtered
    // out of the payload by `hasTeamField` rather than by being blank here.
    teamId: str(event.team?.id),
    // Not the EVENT's zone: this is what clearing a location restores to, and
    // a venue-backed event's own zone came from that venue.
    defaultTimezone,
    // An event with no venue but an address of its own WAS a custom location,
    // so editing one reopens with its address fields showing.
    isCustomLocation:
      !event.venue?.id &&
      Boolean(event.address_name || event.address?.street_1),
    timezone: event.timezone || defaultTimezone,
    arrivalTimeMinutes: idStr(event.arrival_time_minutes),
    // From the nested `series` rule when present — the detail response puts
    // days_of_week and repeat_until there, not at the top level.
    recurrenceType: recurrence?.recurrenceType || 'none',
    // Narrowest scope by default, so saving without touching this field edits
    // only the occurrence the user opened.
    editScope: SERIES_SCOPE_DEFAULT,
    daysOfWeek: recurrence?.daysOfWeek ?? [],
    // Already a UTC ISO instant on both sides — no splitting or recombining.
    repeatUntil: recurrence?.repeatUntil ?? '',
  }
}
